// /app/frontend/src/pages/risk/RiskRegisterPage.jsx
import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { riskLibraryAPI, controlLibraryAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Shield, Plus, Search, Home, ChevronRight, AlertTriangle, Settings,
  Users, DollarSign, Scale, MoreHorizontal, Pencil, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

// Category icons mapping
const CATEGORY_ICONS = {
  'Technical': Settings,
  'Operational': Users,
  'Financial': DollarSign,
  'Rights & Freedoms': Scale,
};

// Score level colors
const getScoreLevel = (score) => {
  if (score >= 16) return { label: 'Severe', color: 'bg-red-500 text-white' };
  if (score >= 12) return { label: 'Serious', color: 'bg-orange-500 text-white' };
  if (score >= 8) return { label: 'Moderate', color: 'bg-yellow-500 text-black' };
  return { label: 'Low', color: 'bg-green-500 text-white' };
};

// Effectiveness dots component
const EffectivenessDots = ({ value }) => {
  const maxDots = 6;
  return (
    <div className="flex gap-1">
      {Array.from({ length: maxDots }).map((_, i) => (
        <div
          key={i}
          className={`w-2 h-2 rounded-full ${
            i < value ? 'bg-emerald-500' : 'bg-gray-200 dark:bg-gray-700'
          }`}
        />
      ))}
    </div>
  );
};

export default function RiskRegisterPage() {
  const { isDPO, isComplianceReadOnly: isReadOnly } = useAuth();
  const [activeTab, setActiveTab] = useState('risks');
  const [risks, setRisks] = useState([]);
  const [controls, setControls] = useState([]);
  const [stats, setStats] = useState({ total: 0, technical: 0, operational: 0, financial: 0, rights_freedoms: 0 });
  const [controlStats, setControlStats] = useState({ total: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Dialog states
  const [riskDialogOpen, setRiskDialogOpen] = useState(false);
  const [controlDialogOpen, setControlDialogOpen] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);
  const [editingControl, setEditingControl] = useState(null);
  
  // Risk form
  const [riskForm, setRiskForm] = useState({
    risk_id: '',
    category: 'Technical',
    risk_name: '',
    description: '',
    default_impact: 3,
    default_likelihood: 3,
  });
  
  // Control form
  const [controlForm, setControlForm] = useState({
    control_id: '',
    control_name: '',
    description: '',
    category: 'Technical',
    impact_reduction: 1,
    likelihood_reduction: 1,
  });

  useEffect(() => {
    fetchData();
  }, [categoryFilter, searchTerm, activeTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [riskStatsRes, controlStatsRes] = await Promise.all([
        riskLibraryAPI.getStats(),
        controlLibraryAPI.getStats(),
      ]);
      setStats(riskStatsRes.data);
      setControlStats(controlStatsRes.data);
      
      if (activeTab === 'risks') {
        const risksRes = await riskLibraryAPI.getAll(
          categoryFilter !== 'all' ? categoryFilter : undefined,
          searchTerm || undefined
        );
        setRisks(risksRes.data);
      } else {
        const controlsRes = await controlLibraryAPI.getAll(
          categoryFilter !== 'all' ? categoryFilter : undefined,
          undefined,
          searchTerm || undefined
        );
        setControls(controlsRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status === 404 || (stats.total === 0 && isDPO)) {
        try {
          await riskLibraryAPI.seed();
          toast.success('Risk library seeded with default data');
          fetchData();
        } catch (seedError) {
          console.error('Failed to seed library:', seedError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCategoryFilter('all');
    setSearchTerm('');
  };

  const handleSeedLibrary = async () => {
    try {
      const res = await riskLibraryAPI.seed();
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to seed library');
    }
  };

  // Risk CRUD
  const openRiskDialog = (risk = null) => {
    if (risk) {
      setEditingRisk(risk);
      setRiskForm({
        risk_id: risk.risk_id,
        category: risk.category,
        risk_name: risk.risk_name,
        description: risk.description,
        default_impact: risk.default_impact || 3,
        default_likelihood: risk.default_likelihood || 3,
      });
    } else {
      setEditingRisk(null);
      setRiskForm({
        risk_id: '',
        category: 'Technical',
        risk_name: '',
        description: '',
        default_impact: 3,
        default_likelihood: 3,
      });
    }
    setRiskDialogOpen(true);
  };

  const handleSaveRisk = async () => {
    try {
      if (editingRisk) {
        await riskLibraryAPI.update(editingRisk.id, riskForm);
        toast.success('Risk updated successfully');
      } else {
        await riskLibraryAPI.create(riskForm);
        toast.success('Risk created successfully');
      }
      setRiskDialogOpen(false);
      setEditingRisk(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save risk');
    }
  };

  const handleDeleteRisk = async (risk) => {
    if (risk.source === 'System') {
      toast.error('Cannot delete system risks');
      return;
    }
    try {
      await riskLibraryAPI.delete(risk.id);
      toast.success('Risk deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete risk');
    }
  };

  // Control CRUD
  const openControlDialog = (control = null) => {
    if (control) {
      setEditingControl(control);
      setControlForm({
        control_id: control.control_id,
        control_name: control.control_name,
        description: control.description,
        category: control.category,
        impact_reduction: control.impact_reduction || 1,
        likelihood_reduction: control.likelihood_reduction || 1,
      });
    } else {
      setEditingControl(null);
      setControlForm({
        control_id: '',
        control_name: '',
        description: '',
        category: 'Technical',
        impact_reduction: 1,
        likelihood_reduction: 1,
      });
    }
    setControlDialogOpen(true);
  };

  const handleSaveControl = async () => {
    try {
      if (editingControl) {
        await controlLibraryAPI.update(editingControl.id, controlForm);
        toast.success('Control updated successfully');
      } else {
        await controlLibraryAPI.create(controlForm);
        toast.success('Control created successfully');
      }
      setControlDialogOpen(false);
      setEditingControl(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save control');
    }
  };

  const handleDeleteControl = async (control) => {
    if (control.source === 'System') {
      toast.error('Cannot delete system controls');
      return;
    }
    try {
      await controlLibraryAPI.delete(control.id);
      toast.success('Control deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete control');
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="risk-register-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Home className="h-4 w-4" />
          <ChevronRight className="h-4 w-4" />
          <span>Risk</span>
          <ChevronRight className="h-4 w-4" />
          <span className="text-foreground font-medium">Register</span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="h-6 w-6 text-primary" />
              Risk Register
            </h1>
            <p className="text-muted-foreground mt-1">
              Organization-wide data protection risk library
            </p>
          </div>
          {!isReadOnly && (
            <div className="flex gap-2">
              {activeTab === 'risks' ? (
                <Button onClick={() => openRiskDialog()} data-testid="add-risk-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Risk
                </Button>
              ) : (
                <Button onClick={() => openControlDialog()} data-testid="add-control-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Control
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 border-blue-200 dark:border-blue-800">
            <CardContent className="p-4">
              <div className="text-sm text-blue-600 dark:text-blue-400 font-medium">Total {activeTab === 'risks' ? 'Risks' : 'Controls'}</div>
              <div className="text-3xl font-bold text-blue-700 dark:text-blue-300">{activeTab === 'risks' ? stats.total : controlStats.total}</div>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer transition-all ${categoryFilter === 'Technical' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setCategoryFilter(categoryFilter === 'Technical' ? 'all' : 'Technical')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4 text-purple-500" />
                <span className="text-sm text-muted-foreground">Technical</span>
              </div>
              <div className="text-2xl font-bold">{stats.technical}</div>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer transition-all ${categoryFilter === 'Operational' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setCategoryFilter(categoryFilter === 'Operational' ? 'all' : 'Operational')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-green-500" />
                <span className="text-sm text-muted-foreground">Operational</span>
              </div>
              <div className="text-2xl font-bold">{stats.operational}</div>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer transition-all ${categoryFilter === 'Financial' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setCategoryFilter(categoryFilter === 'Financial' ? 'all' : 'Financial')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-yellow-500" />
                <span className="text-sm text-muted-foreground">Financial</span>
              </div>
              <div className="text-2xl font-bold">{stats.financial}</div>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer transition-all ${categoryFilter === 'Rights & Freedoms' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setCategoryFilter(categoryFilter === 'Rights & Freedoms' ? 'all' : 'Rights & Freedoms')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Scale className="h-4 w-4 text-red-500" />
                <span className="text-sm text-muted-foreground">Rights & Freedoms</span>
              </div>
              <div className="text-2xl font-bold">{stats.rights_freedoms}</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs and Filters */}
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <Button
              variant={activeTab === 'risks' ? 'default' : 'ghost'}
              onClick={() => handleTabChange('risks')}
              data-testid="risks-tab"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Risks Library ({stats.total})
            </Button>
            <Button
              variant={activeTab === 'controls' ? 'default' : 'ghost'}
              onClick={() => handleTabChange('controls')}
              data-testid="controls-tab"
            >
              <Shield className="h-4 w-4 mr-2" />
              Controls Library ({controlStats.total})
            </Button>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-64"
                data-testid="search-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="Technical">Technical</SelectItem>
                <SelectItem value="Operational">Operational</SelectItem>
                <SelectItem value="Financial">Financial</SelectItem>
                <SelectItem value="Rights & Freedoms">Rights & Freedoms</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Data Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">Loading...</div>
            ) : activeTab === 'risks' ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-24">ID</TableHead>
                    <TableHead className="w-36">Category</TableHead>
                    <TableHead className="w-64">Risk Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-20 text-center">Impact</TableHead>
                    <TableHead className="w-24 text-center">Likelihood</TableHead>
                    <TableHead className="w-24 text-center">Score</TableHead>
                    <TableHead className="w-24">Source</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {risks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        No risks found. {isDPO && (
                          <Button variant="link" onClick={handleSeedLibrary}>
                            Click here to seed the library with default risks.
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    risks.map((risk) => {
                      const impact = risk.default_impact || Math.ceil(Math.sqrt(risk.default_score));
                      const likelihood = risk.default_likelihood || Math.ceil(risk.default_score / impact);
                      const score = risk.default_score || (impact * likelihood);
                      const scoreLevel = getScoreLevel(score);
                      const CategoryIcon = CATEGORY_ICONS[risk.category] || AlertTriangle;
                      return (
                        <TableRow key={risk.id} className="hover:bg-muted/30">
                          <TableCell className="font-mono text-sm font-medium">
                            {risk.risk_id}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <CategoryIcon className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{risk.category}</span>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{risk.risk_name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-md truncate">
                            {risk.description}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">{impact}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">{likelihood}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className={scoreLevel.color}>
                              {score}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={risk.source === 'System' ? 'secondary' : 'outline'}>
                              {risk.source}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`risk-menu-${risk.id}`}>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {!isReadOnly && (
                                  <>
                                    <DropdownMenuItem onClick={() => openRiskDialog(risk)}>
                                      <Pencil className="h-4 w-4 mr-2" /> Edit
                                    </DropdownMenuItem>
                                    {risk.source !== 'System' && (
                                      <DropdownMenuItem
                                        className="text-red-600"
                                        onClick={() => handleDeleteRisk(risk)}
                                      >
                                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                                      </DropdownMenuItem>
                                    )}
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-24">ID</TableHead>
                    <TableHead className="w-64">Control Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-36">Category</TableHead>
                    <TableHead className="w-24 text-center">Impact Red.</TableHead>
                    <TableHead className="w-28 text-center">Likelihood Red.</TableHead>
                    <TableHead className="w-28 text-center">Effectiveness</TableHead>
                    <TableHead className="w-24">Source</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {controls.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        No controls found. {isDPO && (
                          <Button variant="link" onClick={handleSeedLibrary}>
                            Click here to seed the library with default controls.
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ) : (
                    controls.map((control) => {
                      const CategoryIcon = CATEGORY_ICONS[control.category] || Shield;
                      const impactRed = control.impact_reduction || 1;
                      const likelihoodRed = control.likelihood_reduction || 1;
                      return (
                        <TableRow key={control.id} className="hover:bg-muted/30">
                          <TableCell className="font-mono text-sm font-medium">
                            {control.control_id}
                          </TableCell>
                          <TableCell className="font-medium">{control.control_name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-md truncate">
                            {control.description}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <CategoryIcon className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{control.category}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">{impactRed}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline">{likelihoodRed}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <EffectivenessDots value={control.effectiveness || Math.min(6, Math.max(1, Math.ceil((impactRed * likelihoodRed) / 4)))} />
                          </TableCell>
                          <TableCell>
                            <Badge variant={control.source === 'System' ? 'secondary' : 'outline'}>
                              {control.source}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`control-menu-${control.id}`}>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {!isReadOnly && (
                                  <>
                                    <DropdownMenuItem onClick={() => openControlDialog(control)}>
                                      <Pencil className="h-4 w-4 mr-2" /> Edit
                                    </DropdownMenuItem>
                                    {control.source !== 'System' && (
                                      <DropdownMenuItem
                                        className="text-red-600"
                                        onClick={() => handleDeleteControl(control)}
                                      >
                                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                                      </DropdownMenuItem>
                                    )}
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Risk Dialog */}
      <Dialog open={riskDialogOpen} onOpenChange={setRiskDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingRisk ? 'Edit Risk' : 'Add New Risk'}</DialogTitle>
            <DialogDescription>
              {editingRisk ? 'Update the risk details below' : 'Create a new risk entry for your organization'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Risk ID</Label>
                <Input
                  placeholder="e.g., TC-001"
                  value={riskForm.risk_id}
                  onChange={(e) => setRiskForm({ ...riskForm, risk_id: e.target.value })}
                  data-testid="risk-id-input"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select
                  value={riskForm.category}
                  onValueChange={(v) => setRiskForm({ ...riskForm, category: v })}
                >
                  <SelectTrigger data-testid="risk-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Technical">Technical</SelectItem>
                    <SelectItem value="Operational">Operational</SelectItem>
                    <SelectItem value="Financial">Financial</SelectItem>
                    <SelectItem value="Rights & Freedoms">Rights & Freedoms</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Risk Name</Label>
              <Input
                placeholder="Enter risk name"
                value={riskForm.risk_name}
                onChange={(e) => setRiskForm({ ...riskForm, risk_name: e.target.value })}
                data-testid="risk-name-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="Describe the risk..."
                value={riskForm.description}
                onChange={(e) => setRiskForm({ ...riskForm, description: e.target.value })}
                data-testid="risk-description-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Impact (1-5)</Label>
                <Select
                  value={String(riskForm.default_impact)}
                  onValueChange={(v) => setRiskForm({ ...riskForm, default_impact: parseInt(v) })}
                >
                  <SelectTrigger data-testid="risk-impact-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 - Insignificant</SelectItem>
                    <SelectItem value="2">2 - Minor</SelectItem>
                    <SelectItem value="3">3 - Moderate</SelectItem>
                    <SelectItem value="4">4 - Major</SelectItem>
                    <SelectItem value="5">5 - Severe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Likelihood (1-5)</Label>
                <Select
                  value={String(riskForm.default_likelihood)}
                  onValueChange={(v) => setRiskForm({ ...riskForm, default_likelihood: parseInt(v) })}
                >
                  <SelectTrigger data-testid="risk-likelihood-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 - Rare</SelectItem>
                    <SelectItem value="2">2 - Unlikely</SelectItem>
                    <SelectItem value="3">3 - Possible</SelectItem>
                    <SelectItem value="4">4 - Likely</SelectItem>
                    <SelectItem value="5">5 - Almost Certain</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Score</Label>
                <div className="h-10 flex items-center justify-center rounded-md border bg-muted">
                  <Badge className={getScoreLevel(riskForm.default_impact * riskForm.default_likelihood).color}>
                    {riskForm.default_impact * riskForm.default_likelihood}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRiskDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveRisk} data-testid="save-risk-btn">
              {editingRisk ? 'Update Risk' : 'Create Risk'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Control Dialog */}
      <Dialog open={controlDialogOpen} onOpenChange={setControlDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingControl ? 'Edit Control' : 'Add New Control'}</DialogTitle>
            <DialogDescription>
              {editingControl ? 'Update the control details below' : 'Create a new control entry for your organization'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Maps to Risk ID</Label>
                <Input
                  placeholder="e.g., TC-001"
                  value={controlForm.control_id}
                  onChange={(e) => setControlForm({ ...controlForm, control_id: e.target.value })}
                  data-testid="control-id-input"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select
                  value={controlForm.category}
                  onValueChange={(v) => setControlForm({ ...controlForm, category: v })}
                >
                  <SelectTrigger data-testid="control-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Technical">Technical</SelectItem>
                    <SelectItem value="Operational">Operational</SelectItem>
                    <SelectItem value="Financial">Financial</SelectItem>
                    <SelectItem value="Rights & Freedoms">Rights & Freedoms</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Control Name</Label>
              <Input
                placeholder="Enter control name"
                value={controlForm.control_name}
                onChange={(e) => setControlForm({ ...controlForm, control_name: e.target.value })}
                data-testid="control-name-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="Describe the control..."
                value={controlForm.description}
                onChange={(e) => setControlForm({ ...controlForm, description: e.target.value })}
                data-testid="control-description-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Impact Reduction (1-5)</Label>
                <Select
                  value={String(controlForm.impact_reduction)}
                  onValueChange={(v) => setControlForm({ ...controlForm, impact_reduction: parseInt(v) })}
                >
                  <SelectTrigger data-testid="control-impact-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 - Minimal</SelectItem>
                    <SelectItem value="2">2 - Low</SelectItem>
                    <SelectItem value="3">3 - Moderate</SelectItem>
                    <SelectItem value="4">4 - High</SelectItem>
                    <SelectItem value="5">5 - Maximum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Likelihood Reduction (1-5)</Label>
                <Select
                  value={String(controlForm.likelihood_reduction)}
                  onValueChange={(v) => setControlForm({ ...controlForm, likelihood_reduction: parseInt(v) })}
                >
                  <SelectTrigger data-testid="control-likelihood-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 - Minimal</SelectItem>
                    <SelectItem value="2">2 - Low</SelectItem>
                    <SelectItem value="3">3 - Moderate</SelectItem>
                    <SelectItem value="4">4 - High</SelectItem>
                    <SelectItem value="5">5 - Maximum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Effectiveness</Label>
                <div className="h-10 flex items-center justify-center rounded-md border bg-muted">
                  <EffectivenessDots value={Math.min(6, Math.max(1, Math.ceil((controlForm.impact_reduction * controlForm.likelihood_reduction) / 4)))} />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setControlDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveControl} data-testid="save-control-btn">
              {editingControl ? 'Update Control' : 'Create Control'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
